import PanoramaIcon from "@mui/icons-material/Panorama";
import CircularProgress from "@mui/material/CircularProgress";
import ThreeDots from "designSystem/components/progress/ThreeDots";
import { Box, Button, ErrorDialog, FlexRowCenter, FormField, Grid, GridColumn, GridRow, Message, MessageDialog, ModalDialog, MultiSelect, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { blue } from "designSystem/styles/color";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import css from "./Generate3DImagesDialog.module.css";
import Generate3DImagesDialogStore, { Generate3DImagesDialogStoreContext, LIMIT_FOR_MULTISELECT, useGenerate3DImagesDialogStore } from "./Generate3DImagesDialogStore";

interface Generate3DImagesDialogProps {
	/** store for generate images dialog */
	store: Generate3DImagesDialogStore;
}

const Generate3DImagesDialogContent: FunctionComponent = observer(() => {
	const { 
		nodeType,
		documentComponents, 
		templateComponents, 
		documentsGoToDialogAction,
		templatesGoToDialogAction, 
		updateSelectedDocumentUrls,
		selectedDocumentComponents, 
		updateSelectedTemplateUrls, 
		selectedTemplateComponents,
		documentsExceedMaxValue,
		templatesExceedMaxValue,
	} = useGenerate3DImagesDialogStore();
	const locale = useLocale();
	const text = locale.type(nodeType, false, true);
	const documentExceedMaxValueErrMsg = documentsExceedMaxValue ? locale.resource("3DSelectDocumentLimit", [LIMIT_FOR_MULTISELECT]) : "";
	const templateExceedMaxValueErrMsg = templatesExceedMaxValue ? locale.resource("3DSelectTemplateLimit", [LIMIT_FOR_MULTISELECT]) : "";

	const documentsSelectAction = documentComponents.length > 0 
		? 
		[
			<Button
				design="link"
				onClick={documentsGoToDialogAction}
				res="GoToDialog"
			/>,
		] 
		: null
	;
	const templatesSelectAction = templateComponents.length > 0 
		? 
		[
			<Button
				design="link"
				onClick={templatesGoToDialogAction}
				res="GoToDialog"
			/>,
		] 
		: null
	;
 
	return (
		<Grid>
			<GridColumn marginBottom={4}>
				<Text res="3DSelectDocumentGenerateImages" semiBold />
				<Text res="3DSelectDocumentDescriptionGenerateImages" resParams={[text]} />
			</GridColumn>
			<GridColumn>
				<FormField
					className={css.label}
					control={(
						<MultiSelect
							ImageProps={{
								noImageComponent: <PanoramaIcon />,
							}}
							disableClearable
							noChips
							onValueChange={updateSelectedDocumentUrls}
							options={documentComponents}
							placeHolderRes="3DSelectDocumentLimit"
							placeHolderResParams={[LIMIT_FOR_MULTISELECT]}
							selectActions={documentsSelectAction}
							slimCard
							value={selectedDocumentComponents}
						/>
					)}
					fullWidth
					invalidMessage={documentExceedMaxValueErrMsg}
					labelPlacement="top"
					res="Document.Plural"
				/>
				<FormField
					className={css.label}
					control={(
						<MultiSelect
							disableClearable
							noChips
							onValueChange={updateSelectedTemplateUrls}
							options={templateComponents}
							placeHolderRes="3DSelectTemplateLimit"
							placeHolderResParams={[LIMIT_FOR_MULTISELECT]}
							selectActions={templatesSelectAction}
							slimCard
							value={selectedTemplateComponents}
						/>
					)}
					fullWidth
					invalidMessage={templateExceedMaxValueErrMsg}
					labelPlacement="top"
					res="SnapshotTemplate.Plural"
				/>
			</GridColumn>
		</Grid>
	);
});

const GenerateAndCancelButtons: FunctionComponent = observer(() => {
	const { 
		generateImages,
		onCancel,
		canGenerateImages,
		numberOfImagesText,
		selectedTemplateComponents,
		isLoadingNumberOfImages,
	} = useGenerate3DImagesDialogStore();
	
	return (
		<GridRow justifyContent="flex-end" spacing={2}>
			{
				isLoadingNumberOfImages && selectedTemplateComponents.length ? (
					<Button
						design="primary"
						sx={{
							minWidth: 90,
						}}
					>
						<FlexRowCenter justifyContent="center">
							<ThreeDots color={blue[200]} size={4} />
						</FlexRowCenter>
					</Button>
				) : (
					<Button
						design="primary"
						disabled={canGenerateImages}
						onClick={generateImages}
						res="3DGenerate"
						resParams={[numberOfImagesText]}
					/>
				)
			}
			<Button
				design="link"
				onClick={onCancel}
				res="Cancel"
			/>
		</GridRow>
	);
});
 
const Generate3DImagesDialog: FunctionComponent<Generate3DImagesDialogProps> = observer((props) => {
	const { store } = props;
	const { 
		isLoadingImages,
		 numberOfImagesText,
		onCancel,
		errorOnSave,
		closeErrorDialog,
		abortImageGeneration,
		nodeType,
		operationHasAborted,
		invalidCombinations,
	} = store;

	return (
		<Generate3DImagesDialogStoreContext.Provider value={store}>
			<ModalDialog
				contentSpacing={0}
				maxWidth="sm"
				onClose={onCancel}
				open={true && !errorOnSave}
				showClose
				showDividers
				titleRes="GenerateImages"
			>
				{{
					message: (
						invalidCombinations.length ? (
							<Message
								bulletPoints={invalidCombinations}
								titleRes="3DTemplateInvalidCombinationMessage"
								variant="warning"
							/>
						) : null
					),
					buttons: (
						!isLoadingImages && (
							<GenerateAndCancelButtons />
						) 
					),
					main: (
						<Box padding={4}>
							{!isLoadingImages 
								? <Generate3DImagesDialogContent />
								:(
									<MessageDialog
										category={null}
										messageRes= "3DGeneratingNumImages"
										messageResParams={[numberOfImagesText]}
										open={isLoadingImages}
										titleRes="3DGeneratingImages"
									>
				 					{{
											icon: <CircularProgress size={20} />,
											buttons: ( 
												<Button
													design="link"
													onClick={abortImageGeneration}
													res="Cancel"
												/>
											),
										}}
									</MessageDialog>
								)
							}
						</Box>
					),
				}}
			</ModalDialog>
			<ErrorDialog
				error={errorOnSave}
				messageRes={operationHasAborted ? "3DGenerateImagesAbortMessage" : null}
				messageResParams={[ nodeType ]}
				onClose={closeErrorDialog}
				open={!!errorOnSave}
				titleRes={operationHasAborted ? "3DGenerateImagesAbortTitle" : "3DGeneratingImages"}
			/>
		</Generate3DImagesDialogStoreContext.Provider>
	);
});

export default Generate3DImagesDialog;